/**
 * DataTransformerPanel - Visual data transformation studio
 * ZAPHUB 2.0 - JSONPath, RegEx, Templates, Live Preview
 */

import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Code,
  Wand2,
  Play,
  Copy,
  Download,
  Upload,
  AlertCircle,
  CheckCircle,
  Filter,
  SortAsc,
  Group,
  Calculator,
  FileJson,
  FileCode,
  Braces,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

type TransformationType =
  | 'jsonpath'
  | 'regex'
  | 'template'
  | 'map'
  | 'filter'
  | 'sort'
  | 'group'
  | 'aggregate';

export const DataTransformerPanel: React.FC = () => {
  const [transformType, setTransformType] = useState<TransformationType>('jsonpath');
  const [inputData, setInputData] = useState(
    JSON.stringify(
      {
        users: [
          { id: 1, name: 'John Doe', email: 'john@example.com', age: 30, role: 'admin' },
          { id: 2, name: 'Jane Smith', email: 'jane@example.com', age: 25, role: 'user' },
          { id: 3, name: 'Bob Johnson', email: 'bob@example.com', age: 35, role: 'user' },
        ],
        metadata: {
          total: 3,
          timestamp: '2024-12-18T10:00:00Z',
        },
      },
      null,
      2
    )
  );
  const [outputData, setOutputData] = useState('');
  const [error, setError] = useState('');

  // JSONPath
  const [jsonPath, setJsonPath] = useState('$.users[*].name');

  // RegEx
  const [regexPattern, setRegexPattern] = useState('\\d+');
  const [regexReplacement, setRegexReplacement] = useState('***');
  const [regexFlags, setRegexFlags] = useState('g');

  // Template
  const [template, setTemplate] = useState('Hello {{name}}, your email is {{email}}');

  // Map
  const [mappingRules, setMappingRules] = useState([
    { source: '$.users[0].name', target: 'userName' },
    { source: '$.users[0].email', target: 'userEmail' },
  ]);

  // Filter
  const [filterField, setFilterField] = useState('age');
  const [filterOperator, setFilterOperator] = useState<
    'equals' | 'contains' | 'greater_than' | 'less_than' | 'not_equals'
  >('greater_than');
  const [filterValue, setFilterValue] = useState('25');

  // Sort
  const [sortField, setSortField] = useState('age');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Group
  const [groupField, setGroupField] = useState('role');

  // Aggregate
  const [aggField, setAggField] = useState('age');
  const [aggOperation, setAggOperation] = useState<'sum' | 'avg' | 'min' | 'max' | 'count'>('avg');

  const handleTransform = async () => {
    try {
      setError('');
      let result: any;
      const data = JSON.parse(inputData);

      switch (transformType) {
        case 'jsonpath':
          result = await applyJSONPath(data, jsonPath);
          break;
        case 'regex':
          result = await applyRegex(inputData, regexPattern, regexReplacement, regexFlags);
          break;
        case 'template':
          result = await applyTemplate(template, data);
          break;
        case 'map':
          result = await applyMapping(data, mappingRules);
          break;
        case 'filter':
          result = await applyFilter(
            data.users || data,
            filterField,
            filterOperator,
            filterValue
          );
          break;
        case 'sort':
          result = await applySort(data.users || data, sortField, sortOrder);
          break;
        case 'group':
          result = await applyGroupBy(data.users || data, groupField);
          break;
        case 'aggregate':
          result = await applyAggregate(data.users || data, aggField, aggOperation);
          break;
      }

      setOutputData(
        typeof result === 'object' ? JSON.stringify(result, null, 2) : String(result)
      );
    } catch (err) {
      setError((err as Error).message);
      setOutputData('');
    }
  };

  // Transformation functions (simplified)
  const applyJSONPath = async (data: any, path: string): Promise<any> => {
    if (path === '$') return data;

    const parts = path.replace('$', '').split('.').filter(Boolean);
    let result = data;

    for (const part of parts) {
      if (part === '*' || part.includes('[*]')) {
        // Handle wildcards
        const key = part.replace('[*]', '');
        if (key && result[key]) {
          result = result[key];
        }
        if (Array.isArray(result)) {
          return result;
        }
      } else {
        const arrayMatch = part.match(/^(\w+)\[(\d+)\]$/);
        if (arrayMatch) {
          const [, key, index] = arrayMatch;
          result = result[key]?.[parseInt(index)];
        } else {
          result = result[part];
        }
      }
    }

    return result;
  };

  const applyRegex = async (
    input: string,
    pattern: string,
    replacement: string,
    flags: string
  ): Promise<string> => {
    const regex = new RegExp(pattern, flags);
    return input.replace(regex, replacement);
  };

  const applyTemplate = async (template: string, data: any): Promise<string> => {
    let result = template;
    const matches = template.match(/\{\{([^}]+)\}\}/g);

    if (matches) {
      for (const match of matches) {
        const key = match.replace(/[{}]/g, '').trim();
        const value = await applyJSONPath(data, `$.${key}`);
        result = result.replace(match, value !== null ? String(value) : '');
      }
    }

    return result;
  };

  const applyMapping = async (
    data: any,
    rules: Array<{ source: string; target: string }>
  ): Promise<any> => {
    const result: any = {};

    for (const rule of rules) {
      result[rule.target] = await applyJSONPath(data, rule.source);
    }

    return result;
  };

  const applyFilter = async (
    data: any[],
    field: string,
    operator: string,
    value: any
  ): Promise<any[]> => {
    return data.filter((item) => {
      const fieldValue = item[field];

      switch (operator) {
        case 'equals':
          return fieldValue == value;
        case 'not_equals':
          return fieldValue != value;
        case 'contains':
          return String(fieldValue).includes(String(value));
        case 'greater_than':
          return fieldValue > parseFloat(value);
        case 'less_than':
          return fieldValue < parseFloat(value);
        default:
          return true;
      }
    });
  };

  const applySort = async (data: any[], field: string, order: 'asc' | 'desc'): Promise<any[]> => {
    return [...data].sort((a, b) => {
      const aVal = a[field];
      const bVal = b[field];
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const applyGroupBy = async (data: any[], field: string): Promise<any> => {
    const groups: any = {};

    for (const item of data) {
      const key = String(item[field]);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
    }

    return groups;
  };

  const applyAggregate = async (
    data: any[],
    field: string,
    operation: string
  ): Promise<number> => {
    const values = data.map((item) => Number(item[field])).filter((v) => !isNaN(v));

    switch (operation) {
      case 'sum':
        return values.reduce((sum, val) => sum + val, 0);
      case 'avg':
        return values.reduce((sum, val) => sum + val, 0) / values.length;
      case 'min':
        return Math.min(...values);
      case 'max':
        return Math.max(...values);
      case 'count':
        return values.length;
      default:
        return 0;
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(outputData);
    alert('Copied to clipboard!');
  };

  const handleDownload = () => {
    const blob = new Blob([outputData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transformed-data.json';
    a.click();
  };

  const commonJSONPaths = [
    { label: 'All users', value: '$.users' },
    { label: 'User names', value: '$.users[*].name' },
    { label: 'User emails', value: '$.users[*].email' },
    { label: 'First user', value: '$.users[0]' },
    { label: 'Metadata', value: '$.metadata' },
  ];

  return (
    <div className="h-full overflow-auto bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-3xl mb-2">Data Transformation Studio</h1>
        <p className="text-gray-600">
          Transform, filter, and manipulate data with powerful tools
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Input */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg">Input Data</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Upload className="h-4 w-4 mr-1" />
                Load
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  try {
                    const parsed = JSON.parse(inputData);
                    setInputData(JSON.stringify(parsed, null, 2));
                  } catch {}
                }}
              >
                <Code className="h-4 w-4 mr-1" />
                Format
              </Button>
            </div>
          </div>
          <Textarea
            value={inputData}
            onChange={(e) => setInputData(e.target.value)}
            className="font-mono text-sm min-h-[400px]"
            placeholder="Paste your JSON data here..."
          />
        </Card>

        {/* Output */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg">Output Data</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCopy} disabled={!outputData}>
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownload} disabled={!outputData}>
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
            </div>
          </div>
          <Textarea
            value={outputData}
            readOnly
            className="font-mono text-sm min-h-[400px] bg-gray-50"
            placeholder="Transformed data will appear here..."
          />
          {error && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Transformation Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Transformation Controls */}
      <Card className="p-6">
        <Tabs value={transformType} onValueChange={(v: any) => setTransformType(v)}>
          <TabsList className="grid grid-cols-4 mb-6">
            <TabsTrigger value="jsonpath">
              <Braces className="h-4 w-4 mr-2" />
              JSONPath
            </TabsTrigger>
            <TabsTrigger value="regex">
              <Code className="h-4 w-4 mr-2" />
              RegEx
            </TabsTrigger>
            <TabsTrigger value="template">
              <FileCode className="h-4 w-4 mr-2" />
              Template
            </TabsTrigger>
            <TabsTrigger value="map">
              <ArrowRight className="h-4 w-4 mr-2" />
              Map
            </TabsTrigger>
          </TabsList>

          <TabsList className="grid grid-cols-4 mb-6">
            <TabsTrigger value="filter">
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </TabsTrigger>
            <TabsTrigger value="sort">
              <SortAsc className="h-4 w-4 mr-2" />
              Sort
            </TabsTrigger>
            <TabsTrigger value="group">
              <Group className="h-4 w-4 mr-2" />
              Group
            </TabsTrigger>
            <TabsTrigger value="aggregate">
              <Calculator className="h-4 w-4 mr-2" />
              Aggregate
            </TabsTrigger>
          </TabsList>

          <TabsContent value="jsonpath" className="space-y-4">
            <div>
              <Label>JSONPath Expression</Label>
              <Input
                value={jsonPath}
                onChange={(e) => setJsonPath(e.target.value)}
                placeholder="$.users[*].name"
                className="font-mono"
              />
              <p className="text-sm text-gray-600 mt-1">
                Extract specific data from your JSON
              </p>
            </div>

            <div>
              <Label className="mb-2 block">Common Expressions</Label>
              <div className="grid grid-cols-2 gap-2">
                {commonJSONPaths.map((path) => (
                  <Button
                    key={path.value}
                    variant="outline"
                    size="sm"
                    onClick={() => setJsonPath(path.value)}
                    className="justify-start"
                  >
                    {path.label}
                  </Button>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="regex" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Pattern</Label>
                <Input
                  value={regexPattern}
                  onChange={(e) => setRegexPattern(e.target.value)}
                  placeholder="\d+"
                  className="font-mono"
                />
              </div>
              <div>
                <Label>Replacement</Label>
                <Input
                  value={regexReplacement}
                  onChange={(e) => setRegexReplacement(e.target.value)}
                  placeholder="***"
                />
              </div>
            </div>

            <div>
              <Label>Flags</Label>
              <Input
                value={regexFlags}
                onChange={(e) => setRegexFlags(e.target.value)}
                placeholder="g"
                className="font-mono"
              />
              <p className="text-sm text-gray-600 mt-1">g = global, i = case insensitive, m = multiline</p>
            </div>
          </TabsContent>

          <TabsContent value="template" className="space-y-4">
            <div>
              <Label>Template</Label>
              <Textarea
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                placeholder="Hello {{name}}, your email is {{email}}"
                className="font-mono"
              />
              <p className="text-sm text-gray-600 mt-1">
                Use {'{{field}}'} syntax to insert data
              </p>
            </div>
          </TabsContent>

          <TabsContent value="map" className="space-y-4">
            <div>
              <Label className="mb-2 block">Field Mapping</Label>
              {mappingRules.map((rule, idx) => (
                <div key={idx} className="grid grid-cols-2 gap-2 mb-2">
                  <Input
                    value={rule.source}
                    onChange={(e) => {
                      const newRules = [...mappingRules];
                      newRules[idx].source = e.target.value;
                      setMappingRules(newRules);
                    }}
                    placeholder="Source path"
                    className="font-mono text-sm"
                  />
                  <Input
                    value={rule.target}
                    onChange={(e) => {
                      const newRules = [...mappingRules];
                      newRules[idx].target = e.target.value;
                      setMappingRules(newRules);
                    }}
                    placeholder="Target field"
                  />
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMappingRules([...mappingRules, { source: '', target: '' }])}
              >
                Add Mapping
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="filter" className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Field</Label>
                <Input value={filterField} onChange={(e) => setFilterField(e.target.value)} />
              </div>
              <div>
                <Label>Operator</Label>
                <Select value={filterOperator} onValueChange={(v: any) => setFilterOperator(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equals">Equals</SelectItem>
                    <SelectItem value="not_equals">Not Equals</SelectItem>
                    <SelectItem value="contains">Contains</SelectItem>
                    <SelectItem value="greater_than">Greater Than</SelectItem>
                    <SelectItem value="less_than">Less Than</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Value</Label>
                <Input value={filterValue} onChange={(e) => setFilterValue(e.target.value)} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="sort" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Field</Label>
                <Input value={sortField} onChange={(e) => setSortField(e.target.value)} />
              </div>
              <div>
                <Label>Order</Label>
                <Select value={sortOrder} onValueChange={(v: any) => setSortOrder(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">Ascending</SelectItem>
                    <SelectItem value="desc">Descending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="group" className="space-y-4">
            <div>
              <Label>Group By Field</Label>
              <Input value={groupField} onChange={(e) => setGroupField(e.target.value)} />
            </div>
          </TabsContent>

          <TabsContent value="aggregate" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Field</Label>
                <Input value={aggField} onChange={(e) => setAggField(e.target.value)} />
              </div>
              <div>
                <Label>Operation</Label>
                <Select value={aggOperation} onValueChange={(v: any) => setAggOperation(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sum">Sum</SelectItem>
                    <SelectItem value="avg">Average</SelectItem>
                    <SelectItem value="min">Minimum</SelectItem>
                    <SelectItem value="max">Maximum</SelectItem>
                    <SelectItem value="count">Count</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end pt-6 border-t mt-6">
          <Button onClick={handleTransform} size="lg">
            <Play className="h-5 w-5 mr-2" />
            Transform Data
          </Button>
        </div>
      </Card>
    </div>
  );
};
